import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import StyledInput from '../Components/ui/styled-input';
import ErrorAlert from '../Components/ui/error-alert';
import { ContentCard } from "../Components/ContentCard";
import { useSignIn } from './hooks/useSignIn';
import { useAuth } from './authprovider';

interface PasswordFieldProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
}

const PasswordField: React.FC<PasswordFieldProps> = ({ value, onChange, placeholder, required }) => {
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  return (
    <InputGroup>
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        bg="white"
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        color="gray.900"
        height="48px"
        px="1rem"
        fontSize="md"
        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
        _placeholder={{ color: "gray.400" }}
      />
      <InputRightElement h="48px">
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={show ? 'Hide password' : 'Show password'}
          icon={show ? <LuEyeOff /> : <LuEye />}
          onClick={handleClick}
        />
      </InputRightElement>
    </InputGroup>
  );
};

interface SignInFormProps {
  successPath: string;
  onSuccess?: () => void;
}

export const SignInForm: React.FC<SignInFormProps> = ({ successPath, onSuccess }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/account');
    }
  }, [isLoggedIn, navigate]);

  const [
    { email, password, error, isLoading, showSessionAlert, sessionErrorMessage },
    { setEmail, setPassword, handleSubmit, dismissSessionAlert }
  ] = useSignIn({ successPath, onSuccess });

  return (
    <Container centerContent maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <VStack spacing={8}>
        <VStack spacing={6}>
          <Heading as="h1" size="2xl" fontWeight="bold" lineHeight="1.1" color="gray.900">
            Ventus Proxy
          </Heading>
          <Text fontSize="lg" fontWeight="medium" textAlign="center" color="gray.600">
            Welcome back! Sign in to your account
          </Text>
        </VStack>

        <ErrorAlert
          status="warning"
          title="Session expired"
          message={showSessionAlert ? (sessionErrorMessage || 'Your session has expired. Please log in again to continue.') : null}
          onClose={dismissSessionAlert}
        />

        <ContentCard>
          <VStack as="form" onSubmit={handleSubmit} spacing={6} width="md">
            <FormControl>
              <FormLabel>Email</FormLabel>
              <StyledInput
                type="email"
                defaultValue={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder="Enter your email"
                isRequired
              />
            </FormControl>

            <FormControl>
              <FormLabel>Password</FormLabel>
              <PasswordField
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </FormControl>

            <Stack direction="row" justify="flex-start" align="center" width="full">
              <Checkbox defaultChecked colorScheme="blue">Remember me</Checkbox>
            </Stack>

            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              fontWeight="semibold"
              borderRadius="md"
              isLoading={isLoading}
              width="full"
            >
              Sign in
            </Button>

            <Stack align="center" width="full">
              <Link to="/forgot-password">
                <Text color="blue.600" _hover={{ textDecoration: 'underline' }}>
                  Forgot password?
                </Text>
              </Link>
            </Stack>

            <Stack pt="2" align="center">
              <Text>
                Don't have an account?{' '}
                <Link to="/signup">
                  <Text as="span" color="blue.600" _hover={{ textDecoration: 'underline' }}>
                    Sign up
                  </Text>
                </Link>
              </Text>
            </Stack>

            <ErrorAlert
              status="error"
              message={typeof error === 'string' ? error : (error ? 'An error occurred' : null)}
              onClose={() => {}}
              mt={4}
            />
          </VStack>
        </ContentCard>
      </VStack>
    </Container>
  );
};

export default SignInForm;
